<template>
  <global-dialog-card v-model="dialog" :title="$t('delivery_address')" hide-actions>
    <global-confirm-box ref="confirmBox"></global-confirm-box>
    <v-row>
      <v-col cols="6">
        <div>
          <p class="user-location-label" v-if="userLocation">
            {{ $t("all_your_locations") }}
          </p>
          <p
            class="mb-1 user-location-row"
            v-for="address in allUserLocations"
            :key="address.id"
          >
            <span>{{ toLatin(address.address) }}</span>
            <span style="display: inline-flex; align-items: center;">
              <remove-button 
                @click="deleteLocationConfirm(address)" 
                :disabled="deletingLocationId === address.id"
              ></remove-button>
            </span>
          </p>
          <br />
        </div>
        <div>
          <p class="add-location-label mb-0" v-if="userLocation">
            {{ $t("add_new_location") }}
          </p>
          <text-field
            :label="$t('enter_address')"
            v-model="localNewUserLocation.address"
            @change="emitUpdate"
          />
          <p class="coordinates-label mb-0">{{ $t("coordinates") }} :</p>
          <text-field
            :label="$t('lati')"
            v-model="localNewUserLocation.latitude"
            @change="emitUpdate"
          />
          <text-field
            :label="$t('long')"
            v-model="localNewUserLocation.longitude"
            @change="emitUpdate"
          />

          <primary-button 
            @click="addUserLocation" 
            :loading="addingLocation"
            :disabled="!localNewUserLocation.address || !localNewUserLocation.latitude || !localNewUserLocation.longitude"
            block
          >
            {{ $t("add_new_location") }}
          </primary-button>
        </div>
      </v-col>
      <v-col cols="6">
        <p>
          {{ $t('distance_from_restaurant') }}
          <span v-if="userLocation">{{ userLocation.distance }}</span>
        </p>
        <l-map style="height: 230px" :zoom="zoom" :center="center">
          <l-tile-layer :url="url" :attribution="attribution"></l-tile-layer>
          <l-marker :lat-lng="markerLatLng"></l-marker>
        </l-map>
      </v-col>
    </v-row>
  </global-dialog-card>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";
import userService from "@/services/user";
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import { Icon } from "leaflet";
import { LMap, LTileLayer, LMarker } from "vue2-leaflet";

delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  mixins: [hasModel, hasSnackbar],
  name: "UserLocationDialog",
  components: {
    LMap,
    LTileLayer,
    LMarker,
  },
  computed: {
    ...mapState("companyMenu", [
      "dialogs",
      "userLocation",
      "allUserLocations",
      "newUserLocation",
    ]),
    dialog: {
      get() {
        return this.dialogs.userLocation;
      },
      set(val) {
        this.setDialog({ dialog: "userLocation", value: val });
      },
    },
  },
  data() {
    return {
      localNewUserLocation: { ...this.newUserLocation },
      addingLocation: false,
      deletingLocationId: null,
      zoom: 15,
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: "",
      center: [44.78624721128064, 17.216139767293512],
      markerLatLng: [44.78624721128064, 17.216139767293512],
    };
  },
  watch: {
    newUserLocation: {
      handler(val) {
        this.localNewUserLocation = { ...val };
      },
      deep: true,
    },
    localNewUserLocation: {
      handler(val) {
        this.updateNewUserLocation(val);
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations("companyMenu", ["setDialog"]),
    ...mapActions("companyMenu", ["updateAllUserLocations", "updateNewUserLocation"]),
    async addUserLocation() {
      if (!this.localNewUserLocation.address || !this.localNewUserLocation.latitude || !this.localNewUserLocation.longitude) {
        this.showErrorSnackbar(this.$t("please_fill_all_fields"));
        return;
      }
      
      this.addingLocation = true;
      
      let data = {};
      data["user_id"] = this.$store.state.user.user?.id;
      data["new_user_location"] = this.localNewUserLocation;
      
      try {
        const { data: res } = await userService.addNewUserLocation(data);
        this.updateAllUserLocations(res.data);
        this.updateNewUserLocation({
          address: "",
          latitude: "",
          longitude: "",
        });
        this.showSuccessSnackbar(this.$t("location_added"));
      } catch (error) {
        console.error("Error adding location:", error);
        this.showErrorSnackbar(this.$t("error_adding_location"));
      } finally {
        this.addingLocation = false;
      }
    },
    deleteLocationConfirm(address) {
      this.$refs.confirmBox
        .open(
          this.$t("delete_location"),
          this.$t("delete_location_confirmation"),
          { color: "error" }
        )
        .then(() => {
          this.deleteLocation(address);
        })
        .catch(() => {
          // User cancelled
        });
    },
    async deleteLocation(address) {
      if (this.deletingLocationId === address.id) return; // Prevent multiple deletes
      
      this.deletingLocationId = address.id;
      
      try {
        const { data: res } = await userService.deleteLocation({
          address,
        });
        this.updateAllUserLocations(res.data);
        this.showSuccessSnackbar(this.$t("location_deleted"));
      } catch (error) {
        console.error("Error deleting location:", error);
        this.showErrorSnackbar(this.$t("error_deleting_location"));
      } finally {
        this.deletingLocationId = null;
      }
    },
    emitUpdate() {
      // No need to emit, handled by watcher
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_variables.scss";

.close-location-btn {
  position: absolute;
  top: 5px;
  color: white !important;
  right: 5px;
}

.user-location-row {
  font-size: 13px;
  font-weight: 400;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  padding: 2px 0;
}

.delete-location-btn {
  font-size: 11px;
  color: darken($error-color, 10%);
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.delete-disabled {
  pointer-events: none;
  opacity: 0.6;
}

.add-location-label {
  font-size: 13px;
}

.coordinates-label {
  font-size: 13px;
}

.add-location-btn {
  background: $primary-color;
  color: white;
  font-weight: 700;
  font-size: 10px;
  border-radius: 5px;
}

.user-location-label {
  font-size: 13px;
}
</style>
